import { Timestamp } from "api/firebase";
import {
  ICustomProductOptionConfigWithOverrides,
  ICustomProductOptionConfigWithPrice,
  IDraftCustomProductOptionConfig,
  IFile,
  IManagerSupplierToShow,
  IProduct,
  IProductFileAttachment,
  IProductFileWithFile,
  IProductToCategory,
  ISupplierCompany,
  ISurcharge,
  ProductType,
  IUnpackedCustomProductOptionConfigItem,
  LinkedStandProperty,
} from "@evennt-tech/api-v2-core";
import type { IProductCustomConfigsSummary } from "@evennt-tech/api-v2-external";
import type { OrderProductDiscount } from "ts/api/order";
import { UnpackedAttachmentFile } from "./attachmentFile";
import Fee from "./fee";

export enum ShowProductSurchargeType {
  percentage = "percentage",
  fixedFee = "fixedFee",
}

export type ShowProductSurcharge = {
  type: ShowProductSurchargeType;
  amount: number;
  date: Date;
};

export type ShowProductSurchargeWithReferences = Omit<
  ShowProductSurcharge,
  "date"
> & {
  date: Timestamp;
};
export type ShowProductWithReferences = {
  id: string;
  quantity?: number;
  cartQuantity?: number;
  price?: number;
  surcharge?: ISurcharge[];
  isVisible?: boolean;
  hideToBuildTypeIds?: string[];
  purchasedQuantity?: number;
};

export type ShowProduct = Omit<ShowProductWithReferences, "owner"> & {
  owner: ISupplierCompany;
  ownerToShow: IManagerSupplierToShow;
};

export type ProductNoteAttachment = UnpackedAttachmentFile & {
  file?: File;
};

export type ProductNotes = {
  note: string;
  attachments: ProductNoteAttachment[];
};

export type RawNotes = { [k: string]: ProductNotes };

export type ExtraProduct = {
  id: string;
  name: string;
  price: number;
  priceWithFees: number;
  paymentFeeAmount: number | null;
  platformFeeAmount?: number;
  isSelectedByDefault: boolean;
  quantity: number;
  discountAmount?: number;
  discountAmountWithFees?: number;
};

export type CreatingExtraProduct = {
  id: string;
  name: string;
  price: number;
  isSelectedByDefault: boolean;
  quantity: number;
  attachments?: DraftProductAttachment[];
  notes?: ProductNotes;
};

export type ProductWithImage = Product &
  Pick<IDraftCustomProductOptionConfig, "primaryImageId">;

type Product = {
  id: string;
  name: string;
  price: number | null;
  priceWithFees: number;
  description?: string;
  productId?: string;
  approval?: boolean;
  quantity: number;
  showIds?: string[];
  extras?: ExtraProduct[];
  categories: IProductToCategory[];
  fees: Fee[];
  recommendedProducts?: FullProduct[];
  image?: { signedUrl: string };
  invoiceId?: string;
  tagIds?: string[];
  attachments?: IProductFileAttachment[];
  enablePriceOnApplication?: boolean;
  isExhibitorNotesRequired?: boolean;
  linkedStandProperty?: LinkedStandProperty;
  notesPlaceholder?: string;
  isQuickOrder?: boolean;
  purchaseLimit?: number | null;
  supplierCompanyId?: IProduct["supplierCompanyId"];
  cpoGeneratingSince?: IProduct["cpoGeneratingSince"];
};

export type FullProductWithShowValues = Product &
  Omit<ShowProductWithReferences, "price" | "quantity"> & {
    showPrice?: number;
    showQuantity?: number;
    surcharges?: ISurcharge[];
    configId: string;
    customConfigItems?: IUnpackedCustomProductOptionConfigItem[];
    nested?: FullProductWithShowValues[];
    // Enabled CUSTOM configs of a product whose DEFAULT config is disabled
    // (`customConfigsSummary.count`) — lets a consumer page through the real
    // rows (see getCustomConfigsPage) without `nested` populated at all.
    customConfigsCount?: number;
    customConfigsSummary?: IProductCustomConfigsSummary;
    basePrice?: number;
    image?: IProductFileWithFile;
  };

type ProductAmounts = {
  paymentFeeAmount: number | null;
  platformFeeAmount?: number;
  basePrice?: number;
};

export type ProductConfig = ICustomProductOptionConfigWithOverrides &
  ICustomProductOptionConfigWithPrice;

export type ShowProductConfig = ProductConfig &
  ProductAmounts & {
    priceWithFees: number;
    price: number | null;
    image?: IProductFileWithFile;
  };

export type FullProduct = ShowProduct &
  Omit<Product, "images" | "purchaseLimit"> &
  ProductAmounts & {
    notes?: ProductNotes;
    projectCode?: string;
    paymentFeeAmount: number | null;
    platformFeeAmount?: number | null;
    showPrice?: number;
    surcharges?: ISurcharge[];
    configs: ShowProductConfig[];
    cpoConfigId?: string;
    image?: IProductFileWithFile;
    type?: ProductType;
    imageId?: string;
    organiserFee: number;
    discountAmount?: number;
    discountCodes?: OrderProductDiscount[];
    discountAmountWithFees?: number;
    purchaseLimit: number | null;
    customConfigsSummary?: IProductCustomConfigsSummary;
  };

export type LocalStorageExtraProduct = {
  id: string;
  quantity: number;
  notes?: ProductNotes;
};

export type LocalStorageProduct = {
  id: string;
  cartQuantity: number;
  notes?: ProductNotes;
  extras?: LocalStorageExtraProduct[];
  cpoConfigId?: string;
  imageId?: string;
};

// file with this type is saving in db
export type ProductImage = {
  extension: "jpg" | "png" | "jpeg" | "bmp" | "eps" | "gif";
  title: string;
  size: number;
  url: string;
  id: string;
};
// Working shape for the attachments form/upload layer. Holds either a
// persisted attachment (IProductFileAttachment, whose `file` is an IFile) or a
// pending upload (a browser `File` plus a user-entered title), so the same
// react-hook-form field array can carry both before/after saving.
export type DraftProductAttachment = Partial<
  Omit<IProductFileAttachment, "file">
> & {
  id: string;
  title?: string;
  url?: string;
  extension?: string;
  file?: File | IFile;
};

// this type for internal operations
export type RawProductImage = ProductImage & {
  file: File;
};

export enum QuickOrderProductType {
  SELECTED = "SELECTED",
  CUSTOM = "CUSTOM",
}

export type QuickOrderProduct = Omit<Product, "attachments" | "image"> & {
  selectedCategoryIds?: string[];
  image?: File | string;
  imageId?: string;
  attachments?: File[];
  cpoConfig?: { id?: string };
  type: QuickOrderProductType;
  maxEnoughStock?: number;
  isPurchaseLimitReached?: boolean;
};

export type CreateManyProductsArgs = {
  quickOrderProducts: QuickOrderProduct[];
  supplierCompanyId: string;
  showId: string;
};

export default Product;
